import React, { useState } from 'react';
import tstyles from '../branches/SectionTable.module.css';
import styles from './BuildStepsSection.module.css';
import Ico from '../common/Ico';
import Pill from '../common/Pill';
import EmptyState from '../common/EmptyState';
import { SOURCE_COMPONENT_META, getSourceComponentMeta } from '../branches/sourceComponents';
import { statusLabel } from '../branches/statusConfig';
import { getReleaseStepMeta } from './releaseStepConfig';

const ICO_LAYERS    = 'm12 2 10 6-10 6L2 8l10-6z M2 16l10 6 10-6 M2 12l10 6 10-6';
const ICO_CHEVDOWN  = 'm6 9 6 6 6-6';
const ICO_CHEVRIGHT = 'm9 18 6-6-6-6';
const SOURCE_COMPONENT_IDS = Object.keys(SOURCE_COMPONENT_META);
const RUNNING_STATUSES = new Set(['running', 'creating', 'testing', 'validating', 'releasing']);
const SUCCESS_STATUSES = new Set(['completed', 'success', 'released']);
const FAILED_STATUSES = new Set(['errored', 'failed']);

function groupSummary(steps) {
  const failed  = steps.filter((s) => FAILED_STATUSES.has(s.status)).length;
  const running = steps.filter((s) => RUNNING_STATUSES.has(s.status)).length;
  const done    = steps.filter((s) => SUCCESS_STATUSES.has(s.status)).length;

  if (failed > 0)  return { status: 'errored', label: `${failed} failed` };
  if (running > 0) return { status: 'running', label: `${done}/${steps.length} · running` };
  if (done === steps.length) return { status: 'completed', label: `${done}/${steps.length} completed` };
  return { status: 'pending', label: `${done}/${steps.length} pending` };
}

function ComponentGroup({ component, steps, expanded, onToggle, onOpenOp }) {
  const glyph   = getSourceComponentMeta(component);
  const summary = groupSummary(steps);
  const stepsId = `release-build-steps-${component}`;

  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHeader}
        aria-expanded={expanded}
        aria-controls={stepsId}
        onClick={onToggle}
      >
        <span className={styles.compTile} style={{ background: glyph.bg }}>
          {glyph.icon(14)}
        </span>
        <span className={styles.compName}>{glyph.label}</span>
        <Pill status={summary.status} dot>{summary.label}</Pill>
        <Ico
          d={ICO_CHEVDOWN}
          size={14}
          strokeWidth={2}
          className={`${styles.chev} ${expanded ? styles.chevOpen : ''}`}
        />
      </button>

      {expanded && (
        <div className={styles.stepList} id={stepsId}>
          <div className={styles.stepHeaderRow}>
            <span>Step</span><span>Status</span><span className={styles.stepOpHeader}>Operation</span>
          </div>
          {steps.map((step) => {
            const meta = getReleaseStepMeta(component, step.key);
            const clickable = !!step.operation_id;
            return (
              <div
                key={step.key}
                className={`${styles.stepRow}${clickable ? ` ${styles.stepRowClickable}` : ''}`}
                onClick={clickable ? () => onOpenOp?.({
                  component,
                  step: {
                    ...step,
                    label: meta.label,
                    name: meta.name,
                  },
                }) : undefined}
              >
                <span className={styles.stepName}>{meta.label}</span>
                <Pill status={step.status} dot>{statusLabel(step.status)}</Pill>
                <span className={styles.stepOp}>
                  {clickable ? (
                    <Ico d={ICO_CHEVRIGHT} size={16} style={{ color: 'var(--gray-400)' }} />
                  ) : (
                    <span className={styles.noOp}>—</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function BuildStepsSection({ buildStatus, onOpenOp }) {
  const components = SOURCE_COMPONENT_IDS
    .filter((component) => buildStatus?.[component] && typeof buildStatus[component] === 'object')
    .map((component) => [
      component,
      Object.entries(buildStatus[component]).map(([key, step]) => ({
        key,
        ...(step && typeof step === 'object' ? step : {}),
      })),
    ]);
  const stepCount = components.reduce((sum, [, steps]) => sum + steps.length, 0);
  const [expanded, setExpanded] = useState(() => (components[0] ? { [components[0][0]]: true } : {}));

  const toggle = (component) => setExpanded((s) => ({ ...s, [component]: !s[component] }));

  return (
    <section className={tstyles.section} id="sec-build-steps">
      <div className={tstyles.header}>
        <span className={`${tstyles.iconTile} ${styles.buildIconTile}`}>
          <Ico d={ICO_LAYERS} size={16} strokeWidth={1.7} />
        </span>
        <div className={tstyles.headerText}>
          <div className={tstyles.titleRow}>
            <span className={tstyles.title}>Build steps</span>
            <span className={tstyles.countChip}>{stepCount}</span>
          </div>
          <div className={tstyles.subtitle}>
            Per-component build &amp; publish pipeline for the image this release was cut from.
          </div>
        </div>
      </div>

      {components.length === 0 ? (
        <EmptyState
          icon={ICO_LAYERS}
          title="No build steps recorded"
          hint="This release wasn't cut from a tracked build — steps will appear once one is linked."
        />
      ) : (
        <div className={styles.groups}>
          {components.map(([component, steps]) => (
            <ComponentGroup
              key={component}
              component={component}
              steps={steps}
              expanded={!!expanded[component]}
              onToggle={() => toggle(component)}
              onOpenOp={onOpenOp}
            />
          ))}
        </div>
      )}
    </section>
  );
}
